import React, { useState, useEffect, useRef } from "react";

const TwitterCarousel = ({
  downloaded_path,
  videoRef,
  muted,
  onManualUnmute,
  onManualMute
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRefs = useRef([]);
  const [inView, setInView] = useState(true); // Track if the image is in view
  const localRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const videoRefs = useRef([]);
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setInView(entry.isIntersecting);
      },
      { threshold: 0.5 }
    );

    const currentRef = containerRefs.current[currentIndex];
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev < downloaded_path.length - 1 ? prev + 1 : 0
    );
    setIsPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev > 0 ? prev - 1 : downloaded_path.length - 1
    );
    setIsPlaying(false);
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    hasMoved.current = false;
    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;

    const touch = e.touches[0];
    const diffX = touch.clientX - startX.current;
    const diffY = touch.clientY - startY.current;

    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      e.preventDefault(); // block vertical scrolling only for horizontal swipe
      if (!hasMoved.current) {
        if (diffX < 0) nextSlide();
        else prevSlide();
        hasMoved.current = true;
      }
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    hasMoved.current = false;
  };
  const handleMouseDown = (e) => {
    isDragging.current = true;
    hasMoved.current = false;
    startX.current = e.clientX;
    startY.current = e.clientY;
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
  
    const diffX = e.clientX - startX.current;
    const diffY = e.clientY - startY.current;
  
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (!hasMoved.current) {
        if (diffX < 0) nextSlide();
        else prevSlide();
        hasMoved.current = true;
      }
    }
  };
  
  const handleMouseUp = () => {
    isDragging.current = false;
    hasMoved.current = false;
  };  

  const handlePlayPause = (e) => {
    const videoEl = e.currentTarget;
    if (videoEl) {
      if (videoEl.paused) {
        videoEl.play();
        setIsPlaying(true);
      } else {
        videoEl.pause();
        setIsPlaying(false);
      }
    }
  };

  useEffect(() => {
    const handleVideoPlay = () => {
      setIsPlaying(true);
    };

    const handleVideoPause = () => {
      setIsPlaying(false);
    };

    const videoEl = localRef.current;
    if (videoEl) {
      videoEl.addEventListener("play", handleVideoPlay);
      videoEl.addEventListener("pause", handleVideoPause);
    }

    return () => {
      if (videoEl) {
        videoEl.removeEventListener("play", handleVideoPlay);
        videoEl.removeEventListener("pause", handleVideoPause);
      }
    };
  }, [currentIndex]);

  const handleMuteToggle = () => {
    if (muted) {
      onManualUnmute?.();
    } else {
      onManualMute?.();
    }
    videoRefs.current.forEach((el) => {
      if (el) el.muted = !muted;
    });
    if (localRef.current) {
      localRef.current.muted = !muted;
    }
  };

  useEffect(() => {
    const vid = videoRef?.current || localRef.current;
    if (!vid) return;
    vid.muted = muted;
  }, [muted, videoRef]);

  const isVideo = () => {
    const ext = downloaded_path[currentIndex]?.split(".").pop().toLowerCase();
    return ["mp4", "webm", "ogg"].includes(ext);
  };

  return (
    <div
      className="relative mx-auto w-[400px] overflow-hidden select-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="relative w-full h-auto">
        {downloaded_path.map((path, index) => (
          <div
            key={index}
            ref={(el) => (containerRefs.current[index] = el)}
            className={`w-full h-auto ${index === currentIndex ? "block" : "hidden"}`}
          >
            {path.toLowerCase().endsWith(".mp4") ? (
              <div className="relative">
                <video
                  ref={(el) => {
                    if (videoRef) videoRef(el);
                    videoRefs.current[index] = el;
                    if (index === currentIndex) localRef.current = el;
                  }}
                  src={`https://genz-staging.feeltiptop.com/${path}`}
                  muted={muted}
                  loop
                  autoPlay
                  playsInline
                  className="w-full object-cover"
                  onClick={handlePlayPause}
                />
                <div
                  className={`absolute inset-0 bg-black transition-opacity duration-500 pointer-events-none ${
                    isPlaying && inView ? "opacity-0" : "opacity-60"
                  }`}
                />
              </div>
            ) : (
              <div className="relative">
                <img
                  src={`https://genz-staging.feeltiptop.com/${path}`}
                  alt={`Slide ${index + 1}`}
                  className="w-full object-cover"
                  draggable={false}
                />
                <div
                  className={`absolute inset-0 bg-black transition-opacity duration-500 ${
                    inView ? "opacity-0" : "opacity-60"
                  }`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Mute/Unmute Button */}
      {isVideo() && (
        <button
          onClick={handleMuteToggle}
          className="absolute top-2 right-2 p-2 bg-gray-700 text-white rounded-full"
        >
          {muted ? "🔇" : "🔊"}
        </button>
      )}

      {/* Dots for Navigation */}
      <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 transform space-x-1">
        {downloaded_path.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setCurrentIndex(index);
              setIsPlaying(false);
            }}
            className={`h-2 w-2 rounded-full ${
              currentIndex === index ? "bg-white" : "bg-gray-500"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default TwitterCarousel;